/** Reads and writes the user_preferences row through PostgREST, plus the localStorage first-paint cache. */

import { supabase } from '../../utils/supabase/client';
import {
  normalizePreferences,
  sanitisePatch,
  type PreferencesPatch,
  type UserPreferences,
} from './preferencesSchema';

interface BackendError {
  code?: string;
  message?: string;
  status?: number;
}

interface BackendResult<T> {
  data: T | null;
  error: BackendError | null;
}

// The generated Database types predate this table and RPC, so the two calls
// below are typed by hand rather than widening the shared client.
interface PreferencesClient {
  from(table: 'user_preferences'): {
    select(columns: 'prefs'): {
      eq(column: 'user_id', value: string): {
        maybeSingle(): PromiseLike<BackendResult<{ prefs: unknown }>>;
      };
    };
  };
  rpc(fn: 'merge_user_preferences', args: { patch: PreferencesPatch }): PromiseLike<BackendResult<unknown>>;
}

const client = supabase as unknown as PreferencesClient;

// PGRST205/42P01: table missing; PGRST202/42883: function missing. Both mean
// the migration has not reached this project yet, which must not break the app.
const MISSING_BACKEND_CODES = new Set(['PGRST205', 'PGRST202', '42P01', '42883']);

export function isMissingBackend(error: BackendError | null | undefined): boolean {
  if (!error) return false;
  return (error.code !== undefined && MISSING_BACKEND_CODES.has(error.code)) || error.status === 404;
}

export const preferencesCacheKey = (userId: string) => `quantis:preferences:v1:${userId}`;

export function readCache(userId: string): UserPreferences | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(preferencesCacheKey(userId));
    return raw ? normalizePreferences(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function writeCache(userId: string, prefs: UserPreferences): void {
  if (!userId) return;
  try {
    localStorage.setItem(preferencesCacheKey(userId), JSON.stringify(prefs));
  } catch { /* the cache is an optimisation, never a requirement */ }
}

/** The stored preferences, or null when there is no row yet or the backend is not deployed. */
export async function fetchPreferences(userId: string): Promise<UserPreferences | null> {
  const { data, error } = await client.from('user_preferences').select('prefs').eq('user_id', userId).maybeSingle();
  if (error) {
    if (isMissingBackend(error)) return null;
    throw new Error(error.message ?? 'Failed to load preferences');
  }
  return data ? normalizePreferences(data.prefs) : null;
}

/** Merge a patch server-side; returns the merged prefs, or null when the backend is not deployed. */
export async function mergePreferences(patch: PreferencesPatch): Promise<UserPreferences | null> {
  const { data, error } = await client.rpc('merge_user_preferences', { patch: sanitisePatch(patch) });
  if (error) {
    if (isMissingBackend(error)) return null;
    throw new Error(error.message ?? 'Failed to save preferences');
  }
  return normalizePreferences(data);
}
